import { useStore } from '@nanostores/react';
import { BREAKPOINTS, useIsBelow } from '~/lib/hooks/useMediaQuery';
import { useNavigate, useLocation } from 'react-router-dom';
import { chatStore, removeOpenTab, type OpenTab } from '~/lib/stores/chat';
import { $activeGeneration } from '~/lib/stores/activeGeneration';
import { workbenchStore } from '~/lib/stores/workbench';
import { chatId as chatIdStore, description as descriptionAtom } from '~/lib/persistence/useChatHistory';
import { classNames } from '~/utils/classNames';
import { useEditChatDescription } from '~/lib/hooks';
import { SharePreviewButton } from '~/components/workbench/SharePreviewButton';
import { runtime } from '~/lib/runtime';

/** Sandbox state → header status dot + label (mirrors the tab-strip dots). */
const STATUS: Record<string, { dot: string; label: string; pulse?: boolean }> = {
  running: { dot: 'ui-dot-live', label: 'Ready' },
  starting: { dot: 'ui-dot-building', label: 'Building…', pulse: true },
  booting: { dot: 'ui-dot-building', label: 'Starting…', pulse: true },
  sleeping: { dot: 'ui-dot-draft', label: 'Sleeping' },
  error: { dot: 'ui-dot-error', label: 'Error' },
  idle: { dot: 'ui-dot-draft', label: '' },
};

function closeTab(tab: OpenTab, navigate: (to: string) => void) {
  const wasActive = chatStore.get().activeChat === tab.path;
  removeOpenTab(tab.path);

  if (wasActive) {
    const remaining = chatStore.get().openTabs;

    if (remaining.length > 0) {
      const next = remaining[remaining.length - 1];
      chatStore.setKey('activeChat', next.path);
      navigate(next.path);
    } else {
      chatStore.setKey('started', false);
      chatStore.setKey('aborted', false);
      chatStore.setKey('activeChat', '');
      descriptionAtom.set(undefined);
      workbenchStore.showWorkbench.set(false);
      navigate('/');
    }
  }
}

/** Active-tab chat name with inline rename (pencil reveals on tab hover). */
function ActiveTabName() {
  const description = useStore(descriptionAtom);
  const { editing, saving, handleChange, handleBlur, handleSubmit, currentDescription, toggleEditMode } =
    useEditChatDescription({ initialDescription: description ?? '', syncWithGlobalStore: true });

  if (!description && !editing) {
    return null;
  }

  if (editing) {
    return (
      <form onSubmit={handleSubmit} className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
        <input
          type="text"
          autoFocus
          value={currentDescription}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.currentTarget.blur();
            }
          }}
          disabled={saving}
          aria-label="Chat name"
          className="bg-transparent border-b border-accent text-primary outline-none"
          style={{ maxWidth: 160, fontSize: 12 }}
        />
        <button
          type="submit"
          aria-label="Save name"
          title="Save"
          disabled={saving}
          onMouseDown={(e) => {
            if (!saving) {
              handleSubmit(e);
            }
          }}
          className={classNames(
            'flex-shrink-0 transition-colors',
            saving ? 'i-svg-spinners:90-ring-with-bg text-muted' : 'i-ph:check text-muted hover:text-accent',
          )}
          style={{ fontSize: 13 }}
        />
      </form>
    );
  }

  return (
    <span className="inline-flex items-center" style={{ gap: 6, minWidth: 0 }}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{description}</span>
      <button
        type="button"
        aria-label="Rename chat"
        title="Rename chat"
        onClick={(e) => {
          e.stopPropagation();
          toggleEditMode();
        }}
        className="i-ph:pencil-simple opacity-0 group-hover:opacity-60 touch:opacity-60 hover:!opacity-100 transition-opacity"
        style={{ fontSize: 11, flexShrink: 0 }}
      />
    </span>
  );
}

/**
 * Live status, Preview/Code reopen (only while the preview is closed), Publish
 * and Share for the open chat. `compact` is the icon-only variant that lives in
 * AppLayout's mobile top bar, so phones and tablets get one bar, not two.
 */
export function ChatHeaderActions({ compact = false }: { compact?: boolean }) {
  const activeGen = useStore($activeGeneration);
  const location = useLocation();
  const isOnChat = location.pathname.startsWith('/chat/');
  const currentChatId = useStore(chatIdStore);
  const showWorkbench = useStore(workbenchStore.showWorkbench);
  const previews = useStore(workbenchStore.previews);
  const sandboxStatus = useStore(runtime.status);
  const status = STATUS[sandboxStatus] ?? STATUS.idle;
  // True while THIS chat is mid-build — an initial generation OR a resume (the
  // active-generation poll reports both). Publish is hidden until the build
  // finishes, so we never offer to ship a half-generated site.
  const isBuilding = !!activeGen && (activeGen.id === currentChatId || activeGen.urlId === currentChatId);

  if (!isOnChat || !currentChatId) {
    return null;
  }

  if (compact) {
    const iconButton =
      'relative inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-elevated text-primary transition-colors hover:bg-[#2E2E37]';

    return (
      <div className="flex flex-shrink-0 items-center gap-1.5">
        {!showWorkbench && (
          <>
            {/* Below lg the preview doesn't open over the chat by itself — the dot says it's ready. */}
            <button
              type="button"
              className={iconButton}
              onClick={() => workbenchStore.reopenWorkbench()}
              aria-label={previews.length > 0 ? 'Open preview (ready)' : 'Open preview'}
            >
              <span className="i-ph:eye text-[15px] text-accent" />
              {previews.length > 0 && (
                <span className="absolute right-0.5 top-0.5 h-2 w-2 rounded-full bg-accent ring-2 ring-surface" />
              )}
            </button>
            <button
              type="button"
              className={iconButton}
              onClick={() => workbenchStore.reopenWorkbench('code')}
              aria-label="Open code"
            >
              <span className="i-ph:code text-[15px] text-accent" />
            </button>
            {!isBuilding && (
              <button
                type="button"
                onClick={() => workbenchStore.reopenWorkbench('deployment')}
                className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-accent text-[#0A0A0A] shadow-[0_4px_16px_-5px_var(--accent-glow)] transition-[filter] hover:brightness-110"
                aria-label="Publish"
              >
                <span className="i-ph:rocket-launch text-[15px]" />
              </button>
            )}
          </>
        )}

        <SharePreviewButton chatId={currentChatId} />
      </div>
    );
  }

  return (
    <div className="flex flex-shrink-0 items-center gap-2 pl-2">
      {status.label && (
        <span className="mr-0.5 hidden items-center gap-1.5 text-[12px] text-secondary md:inline-flex">
          <span className={classNames('ui-dot', status.dot, status.pulse && 'ui-pulsing')} />
          {status.label}
        </span>
      )}

      {!showWorkbench && (
        <>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-full bg-elevated px-2.5 sm:px-3.5 py-1.5 text-[12px] font-semibold text-primary transition-colors hover:bg-[#2E2E37]"
            onClick={() => workbenchStore.reopenWorkbench()}
            title="Open preview"
          >
            <span className="i-ph:eye text-[13px] text-accent" />
            <span className="hidden sm:inline">Preview</span>
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-full bg-elevated px-2.5 sm:px-3.5 py-1.5 text-[12px] font-semibold text-primary transition-colors hover:bg-[#2E2E37]"
            onClick={() => workbenchStore.reopenWorkbench('code')}
            title="Open code"
          >
            <span className="i-ph:code text-[13px] text-accent" />
            <span className="hidden sm:inline">Code</span>
          </button>

          {/* Header-sized accent pill (the full ui-btn-accent is a hero-size
              CTA — too tall for the bar). Same lime + glow, compact height.
              Hidden while the build is in progress (generating or resuming)
              — only offered once the site is done. */}
          {!isBuilding && (
            <button
              type="button"
              onClick={() => workbenchStore.reopenWorkbench('deployment')}
              className="inline-flex items-center gap-1.5 rounded-full bg-accent px-2.5 sm:px-3.5 py-1.5 text-[12px] font-semibold text-[#0A0A0A] shadow-[0_4px_16px_-5px_var(--accent-glow)] transition-[filter] hover:brightness-110"
            >
              <span className="i-ph:rocket-launch text-[13px]" />
              <span className="hidden sm:inline">Publish</span>
            </button>
          )}
        </>
      )}

      <SharePreviewButton chatId={currentChatId} />
    </div>
  );
}

export function Header() {
  const chat = useStore(chatStore);
  const activeGen = useStore($activeGeneration);
  const navigate = useNavigate();
  const location = useLocation();
  const isOnChat = location.pathname.startsWith('/chat/');
  const isCompact = useIsBelow(BREAKPOINTS.lg);

  // Below lg the actions live in AppLayout's mobile top bar (ChatHeaderActions compact).
  // Unmount rather than hide, so only one ChatHeaderActions/SharePreviewButton exists.
  if (isCompact) {
    return null;
  }

  return (
    <div className="ui-root hidden lg:block">
      <header
        className="flex items-center"
        style={{
          gap: 8,
          padding: '8px 14px',
          borderBottom: '1px solid var(--color-hairline)',
          background: 'var(--color-surface)',
        }}
      >
        {/* Tab strip — scrolls sideways once the open tabs outgrow it */}
        <div
          className="flex items-end overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          style={{ flex: 1, minWidth: 0, gap: 0 }}
        >
          {chat.openTabs.map((tab) => {
            const isActiveTab = isOnChat && chat.activeChat === tab.path;
            const dotClass = isActiveTab ? 'ui-dot-live' : 'ui-dot-draft';
            const isGenerating = !!activeGen && (activeGen.id === tab.chatId || activeGen.urlId === tab.chatId);

            return (
              <div
                key={tab.path}
                className={classNames('flex items-center', { group: isActiveTab })}
                style={{
                  gap: 6,
                  padding: '6px 12px',
                  borderRadius: isActiveTab ? '8px 8px 0 0' : 0,
                  background: isActiveTab ? 'var(--color-sunken)' : 'transparent',
                  border: isActiveTab ? '1px solid var(--color-hairline)' : '1px solid transparent',
                  borderBottom: 0,
                  fontSize: 12,
                  color: isActiveTab ? 'var(--color-primary)' : 'var(--color-muted)',
                  maxWidth: 220,
                  cursor: isActiveTab ? 'default' : 'pointer',
                }}
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (!isActiveTab) {
                    navigate(tab.path);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isActiveTab) {
                    navigate(tab.path);
                  }
                }}
              >
                {isGenerating ? (
                  <span
                    className="i-svg-spinners:90-ring-with-bg"
                    style={{ fontSize: 12, color: 'var(--color-accent)' }}
                    title="Generating…"
                  />
                ) : (
                  <span className={classNames('ui-dot', dotClass)} />
                )}
                <span
                  style={{
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {isActiveTab ? <ActiveTabName /> : tab.description || 'Untitled'}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    closeTab(tab, navigate);
                  }}
                  className="ui-btn-ghost !px-1 !py-px touch:!p-2"
                  style={{ opacity: isActiveTab ? 0.6 : 0.4 }}
                  title="Close tab"
                  aria-label="Close tab"
                >
                  <span className="i-ph:x text-xs" />
                </button>
              </div>
            );
          })}
        </div>

        {/* Right actions — merged into the one header (no separate CHAT sub-header). */}
        <ChatHeaderActions />
      </header>
    </div>
  );
}
